/**
 * Equipment Manager
 * Handles equipment purchases, equipping, and stat calculations
 */

import { PlayerStats, GameEvent, EquipmentSlot, EquipmentStats, calculateStatMultipliers } from '@/types';
import { getEquipmentDefinition, BALANCE_CONFIG } from '@/data';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';

export class EquipmentManager {
  /**
   * Purchase equipment
   */
  purchaseEquipment(equipmentId: string, priceOverride?: number): boolean {
    const equipment = getEquipmentDefinition(equipmentId);
    const state = useGameStore.getState();
    const price = priceOverride ?? equipment.cost;

    // Check requirements (prerequisites and currency)
    if (!this.canPurchase(equipmentId, priceOverride)) {
      console.warn('Cannot purchase equipment - requirements not met');
      return false;
    }

    // Deduct currency
    state.addCurrency(-price);

    // Determine quantity to add (consumables add in bulk)
    const addQty = equipment.purchaseQuantity || 1;

    // Add to owned or increment quantity
    const existing = state.playerState.ownedEquipment[equipmentId];
    if (existing) {
      // Increment quantity
      state.addOwnedEquipment(equipmentId, {
        ...existing,
        quantity: existing.quantity + addQty,
      });
    } else {
      // First purchase
      state.addOwnedEquipment(equipmentId, {
        ownedAt: Date.now(),
        quantity: addQty,
        timesUsed: 0,
      });

      // Auto-equip if slot empty (only on first purchase)
      // Trap bait is never auto-equipped — it's selected via TrapPanel dropdown
      if (!equipment.isTrapBait && !state.playerState.equippedGear[equipment.slot]) {
        this.equipItem(equipmentId);
      }
    }

    // Emit event
    eventBus.emit(GameEvent.EQUIPMENT_PURCHASED, {
      equipmentId,
      price,
    });

    // Save to localStorage after purchase
    saveManager.save();

    return true;
  }

  /**
   * Check if equipment can be purchased
   */
  canPurchase(equipmentId: string, priceOverride?: number): boolean {
    const equipment = getEquipmentDefinition(equipmentId);
    const state = useGameStore.getState();
    const price = priceOverride ?? equipment.cost;

    // Check currency
    if (state.playerState.currency < price) {
      return false;
    }

    // Check prerequisites
    if (equipment.requires?.previousTier) {
      if (!state.playerState.ownedEquipment[equipment.requires.previousTier]) {
        return false;
      }
    }

    if (equipment.requires?.fishCaught) {
      if (
        state.playerState.stats.totalFishCaught < equipment.requires.fishCaught
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Equip an owned item
   */
  equipItem(equipmentId: string): void {
    const equipment = getEquipmentDefinition(equipmentId);
    const state = useGameStore.getState();

    // Verify ownership
    if (!state.playerState.ownedEquipment[equipmentId]) {
      throw new Error('Equipment not owned');
    }

    // Check availability (item might be fully used by buddies)
    // Allow if this item is already equipped by the player in this slot (re-equip/swap)
    const currentlyEquipped = state.playerState.equippedGear[equipment.slot];
    if (currentlyEquipped !== equipmentId && !this.isItemAvailable(equipmentId)) {
      console.warn('Cannot equip - all units are in use by buddies');
      return;
    }

    // Equip
    const newGear = {
      ...state.playerState.equippedGear,
      [equipment.slot]: equipmentId,
    };

    // Auto-unequip bait when switching to lure-type tackle
    if (equipment.slot === EquipmentSlot.TACKLE && equipment.tackleType === 'lure') {
      newGear[EquipmentSlot.BAIT] = null;
    }

    state.setEquippedGear(newGear);

    // Recalculate stats
    this.recalculateStats();

    // Emit event
    eventBus.emit(GameEvent.EQUIPMENT_EQUIPPED, { equipmentId });

    // Save to localStorage after equipping
    saveManager.save();
  }

  /**
   * Unequip item from slot
   */
  unequipItem(slot: EquipmentSlot): void {
    const state = useGameStore.getState();

    state.setEquippedGear({
      ...state.playerState.equippedGear,
      [slot]: null,
    });

    this.recalculateStats();

    // Save to localStorage after unequipping
    saveManager.save();
  }

  /**
   * Alias for backwards compatibility
   */
  unequipSlot(slot: EquipmentSlot): void {
    this.unequipItem(slot);
  }

  // ── Buddy Equipment Methods ──────────────────────────────────────

  /**
   * Get available quantities for each equipment item.
   * Subtracts player-equipped and all buddy-equipped items from owned quantities.
   */
  getAvailableQuantities(): Record<string, number> {
    const state = useGameStore.getState();
    const owned = state.playerState.ownedEquipment;
    const playerGear = state.playerState.equippedGear;
    const buddies = state.playerState.fishingBuddies || [];

    const available: Record<string, number> = {};

    for (const [id, data] of Object.entries(owned)) {
      let used = 0;

      // Count player-equipped
      for (const equipped of Object.values(playerGear)) {
        if (equipped === id) used++;
      }

      // Count buddy-equipped
      for (const buddy of buddies) {
        if (!buddy.hired || !buddy.equippedGear) continue;
        for (const equipped of Object.values(buddy.equippedGear)) {
          if (equipped === id) used++;
        }
      }

      available[id] = Math.max(0, data.quantity - used);
    }

    return available;
  }

  /**
   * Check if an equipment item has available stock (not fully equipped)
   */
  isItemAvailable(equipmentId: string): boolean {
    const available = this.getAvailableQuantities();
    return (available[equipmentId] || 0) > 0;
  }

  /**
   * Equip an item on a fishing buddy
   */
  equipBuddyItem(buddySlotNumber: number, equipmentId: string): boolean {
    const state = useGameStore.getState();

    // Verify ownership first (before looking up definition which throws on invalid IDs)
    if (!state.playerState.ownedEquipment[equipmentId]) {
      console.warn('Cannot equip on buddy - item not owned');
      return false;
    }

    const equipment = getEquipmentDefinition(equipmentId);

    // Verify availability
    if (!this.isItemAvailable(equipmentId)) {
      console.warn('Cannot equip on buddy - no available units');
      return false;
    }

    // Find buddy
    const buddyIndex = state.playerState.fishingBuddies.findIndex(
      (b) => b.slotNumber === buddySlotNumber
    );
    if (buddyIndex === -1) {
      console.warn('Cannot equip on buddy - buddy not found');
      return false;
    }

    useGameStore.setState((draft) => {
      draft.playerState.fishingBuddies[buddyIndex].equippedGear[equipment.slot] = equipmentId;
      // Auto-unequip bait when switching to lure-type tackle
      if (equipment.slot === EquipmentSlot.TACKLE && equipment.tackleType === 'lure') {
        draft.playerState.fishingBuddies[buddyIndex].equippedGear[EquipmentSlot.BAIT] = null;
      }
    });

    eventBus.emit(GameEvent.EQUIPMENT_EQUIPPED, {
      equipmentId,
      buddySlot: buddySlotNumber,
    });

    saveManager.save();
    return true;
  }

  /**
   * Unequip an item from a fishing buddy's slot
   */
  unequipBuddyItem(buddySlotNumber: number, slot: EquipmentSlot): void {
    const state = useGameStore.getState();
    const buddyIndex = state.playerState.fishingBuddies.findIndex(
      (b) => b.slotNumber === buddySlotNumber
    );
    if (buddyIndex === -1) return;

    useGameStore.setState((draft) => {
      draft.playerState.fishingBuddies[buddyIndex].equippedGear[slot] = null;
    });

    saveManager.save();
  }

  /**
   * Calculate summed equipment stats for a buddy
   */
  calculateBuddyEquipmentStats(buddySlotNumber: number): EquipmentStats {
    const state = useGameStore.getState();
    const buddy = state.playerState.fishingBuddies.find(
      (b) => b.slotNumber === buddySlotNumber
    );

    const stats: EquipmentStats = {
      fishingSpeed: 0,
      catchChance: 0,
      qualityBonus: 0,
      valueBonus: 0,
    };

    if (!buddy?.equippedGear) return stats;

    for (const equipmentId of Object.values(buddy.equippedGear)) {
      if (!equipmentId) continue;
      try {
        const equipment = getEquipmentDefinition(equipmentId);
        stats.fishingSpeed! += equipment.stats.fishingSpeed || 0;
        stats.catchChance! += equipment.stats.catchChance || 0;
        stats.qualityBonus! += equipment.stats.qualityBonus || 0;
        stats.valueBonus! += equipment.stats.valueBonus || 0;
      } catch {
        // Skip invalid equipment IDs
      }
    }

    return stats;
  }

  // ── Consumable Methods ─────────────────────────────────────────

  /**
   * Consume one unit of bait for a successful catch.
   * @param source 'player' or buddy slot number
   * @returns info about what was consumed
   */
  consumeBait(source: 'player' | number): { consumed: boolean; baitId?: string; remaining?: number } {
    const state = useGameStore.getState();

    let baitId: string | null = null;

    if (source === 'player') {
      baitId = state.playerState.equippedGear[EquipmentSlot.BAIT];
    } else {
      const buddy = state.playerState.fishingBuddies.find((b) => b.slotNumber === source);
      baitId = buddy?.equippedGear?.[EquipmentSlot.BAIT] || null;
    }

    if (!baitId) return { consumed: false };

    const owned = state.playerState.ownedEquipment[baitId];
    if (!owned || owned.quantity <= 0) return { consumed: false };

    const newQuantity = owned.quantity - 1;

    useGameStore.setState((draft) => {
      const ownedItem = draft.playerState.ownedEquipment[baitId!];
      if (ownedItem) {
        ownedItem.quantity = newQuantity;
      }

      // Auto-unequip if quantity reaches 0
      if (newQuantity <= 0) {
        if (source === 'player') {
          draft.playerState.equippedGear[EquipmentSlot.BAIT] = null;
        } else {
          const buddy = draft.playerState.fishingBuddies.find((b) => b.slotNumber === source);
          if (buddy?.equippedGear) {
            buddy.equippedGear[EquipmentSlot.BAIT] = null;
          }
        }
      }
    });

    // Recalculate player stats when bait is exhausted (removes the buff)
    if (newQuantity <= 0 && source === 'player') {
      this.recalculateStats();
    }

    return { consumed: true, baitId, remaining: newQuantity };
  }

  /**
   * Scavenge worms — adds 1 bait_worms to inventory
   */
  scavengeWorms(): void {
    const state = useGameStore.getState();
    const existing = state.playerState.ownedEquipment['bait_worms'];

    if (existing) {
      state.addOwnedEquipment('bait_worms', {
        ...existing,
        quantity: existing.quantity + 1,
      });
    } else {
      state.addOwnedEquipment('bait_worms', {
        ownedAt: Date.now(),
        quantity: 1,
        timesUsed: 0,
      });
    }
  }

  /**
   * Recalculate derived player stats from equipment AND progression
   */
  recalculateStats(): PlayerStats {
    const state = useGameStore.getState();
    const equippedGear = state.playerState.equippedGear;

    // Calculate equipment-based stats
    const equipmentStats: PlayerStats = {
      fishingSpeed: 0,
      catchChance: 0,
      qualityBonus: 0,
      valueBonus: 0,
      effectiveFishingTime: BALANCE_CONFIG.BASE_FISHING_TIME,
      effectiveCatchRate: BALANCE_CONFIG.BASE_CATCH_CHANCE,
    };

    // Sum stats from all equipped items (skip TRAP slot — trap stats apply to trap catches, not fishing)
    Object.entries(equippedGear).forEach(([slot, equipmentId]) => {
      if (!equipmentId) return;
      if (slot === EquipmentSlot.TRAP) return; // Trap/bait stats don't affect fishing

      const equipment = getEquipmentDefinition(equipmentId);

      equipmentStats.fishingSpeed += equipment.stats.fishingSpeed || 0;
      equipmentStats.catchChance += equipment.stats.catchChance || 0;
      equipmentStats.qualityBonus += equipment.stats.qualityBonus || 0;
      equipmentStats.valueBonus += equipment.stats.valueBonus || 0;
    });

    // Get progression stat multipliers
    const multipliers = calculateStatMultipliers(state.playerState.progression.stats);

    // Apply progression multipliers to equipment stats
    const finalStats: PlayerStats = {
      fishingSpeed: equipmentStats.fishingSpeed,
      catchChance: equipmentStats.catchChance,
      qualityBonus: equipmentStats.qualityBonus + multipliers.qualityBonus,
      valueBonus: equipmentStats.valueBonus + multipliers.valueBonus,
      effectiveFishingTime: BALANCE_CONFIG.BASE_FISHING_TIME,
      effectiveCatchRate: BALANCE_CONFIG.BASE_CATCH_CHANCE,
    };

    // Calculate derived values with progression multipliers
    // Catch speed multiplier reduces fishing time
    finalStats.effectiveFishingTime =
      BALANCE_CONFIG.BASE_FISHING_TIME * (1 - finalStats.fishingSpeed) / multipliers.catchSpeed;
    finalStats.effectiveFishingTime = Math.max(
      finalStats.effectiveFishingTime,
      BALANCE_CONFIG.MIN_FISHING_TIME
    );

    // Catch chance multiplier increases success rate
    finalStats.effectiveCatchRate = Math.min(
      BALANCE_CONFIG.MAX_CATCH_CHANCE,
      (BALANCE_CONFIG.BASE_CATCH_CHANCE + finalStats.catchChance) * multipliers.catchChance
    );

    // Store in state
    state.setPlayerStats(finalStats);

    return finalStats;
  }
}

// Singleton instance
export const equipmentManager = new EquipmentManager();
